"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { BookOpen, ExternalLink, Sparkles } from "lucide-react";
const architects = [{
  name: "Maestra Sofia",
  role: "Storytelling Guru",
  specialty: "Superpower: Puppet Magic",
  bio: "Sofia brings stories to life with her collection of 20+ puppets and a magical imagination that keeps little learners spellbound.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/teacher_sofia.png",
  initials: "SF",
  paperLink: "/OurClassesPage",
  paperTitle: "Watch Sofia's Intro Video"
}, {
  name: "Maestro Diego",
  role: "Music & Rhythm Master",
  specialty: "Superpower: Master of Songs",
  bio: "Diego turns every grammar lesson into a catchy song that kids can't stop singing! He makes rhythm the heart of learning.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/teacher_diego.png",
  initials: "DG",
  paperLink: "/OurClassesPage",
  paperTitle: "Watch Diego's Intro Video"
}, {
  name: "Maestra Lucia",
  role: "Game Wizard",
  specialty: "Superpower: Fun & Games",
  bio: "Lucia transforms Spanish learning into an epic quest filled with interactive challenges and playful rewards for every child.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/teacher_lucia.png",
  initials: "LC",
  paperLink: "/OurClassesPage",
  paperTitle: "Watch Lucia's Intro Video"
}, {
  name: "Maestro Carlos",
  role: "Art & Creativity Lead",
  specialty: "Superpower: Creative Spark",
  bio: "Carlos uses drawing and crafts to help kids visualize their first Spanish words, making every lesson a masterpiece of creativity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1614297/img/teacher_carlos.png",
  initials: "CL",
  paperLink: "/OurClassesPage",
  paperTitle: "Watch Carlos's Intro Video"
}];
export default function TheArchitects() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="97397" id="meet-the-playful-teachers" className="bg-background text-foreground relative overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/15 blur-[120px] rounded-full" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/15 blur-[150px] rounded-full" />
        {}
        <div className="absolute inset-0 opacity-[0.05] mix-blend-overlay bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI4IiBoZWlnaHQ9IjgiPgo8cmVjdCB3aWR0aD0iOCIgaGVpZ2h0PSI4IiBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMSIvPgo8L3N2Zz4=')]" />
      </div>

      <div ref={ref} className={`container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
        <div className="text-center max-w-3xl mx-auto mb-16 lg:mb-24">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary text-sm font-bold mb-6">
            <Sparkles className="size-4" />
            <span>Meet the Adventure Guides</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold tracking-tight mb-6 font-serif text-primary">
            The Playful Teachers
          </h2>
          <p className="text-xl text-muted-foreground font-sans">
            Our teachers are joyful explorers who turn every Spanish lesson into a magical adventure filled with curiosity, confidence, and laughter.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {architects.map((architect, i) => <div key={architect.name} data-index={i} className="flex flex-col items-center group">
              <HoverCard openDelay={200} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <div className="flex flex-col items-center cursor-pointer outline-none rounded-3xl p-6 transition-all duration-500 hover:bg-white/80 hover:backdrop-blur-md border border-transparent hover:border-primary/20 hover:shadow-2xl hover:shadow-primary/10 w-full hover:-translate-y-2">
                    <div className="relative mb-6">
                      <div className="absolute inset-0 bg-gradient-to-tr from-primary to-secondary rounded-3xl blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-500" />
                      <SafeHydrate><Avatar className="size-40 border-8 border-white shadow-xl shadow-black/10 relative z-10 transition-transform duration-500 group-hover:scale-105 rounded-2xl">
                        <AvatarImage src={architect.image} alt={architect.name} className="object-cover" />
                        <AvatarFallback className="text-4xl font-serif bg-secondary/20 text-secondary-foreground">
                          {architect.initials}
                        </AvatarFallback>
                      </Avatar></SafeHydrate>
                    </div>
                    
                    <h3 className="text-2xl font-bold text-foreground mb-1 text-center font-serif">
                      {architect.name}
                    </h3>
                    <p className="text-base font-medium text-secondary-foreground text-center mb-4 h-10">
                      {architect.role}
                    </p>
                    
                    <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20 px-4 py-1 rounded-full font-bold hover:bg-primary/20 transition-colors">
                      {architect.specialty}
                    </Badge>
                  </div>
                </HoverCardTrigger>
                
                <HoverCardContent side="bottom" align="center" className="w-80 bg-white/95 backdrop-blur-xl border-primary/20 shadow-2xl shadow-primary/10 p-6 rounded-3xl">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-bold text-primary mb-2 uppercase tracking-widest">About Teacher</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                        {architect.bio}
                      </p>
                    </div>
                    
                    <div className="pt-4 border-t border-primary/10">
                      <h4 className="text-xs font-bold text-secondary-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                        <BookOpen className="size-3" />
                        Teacher's Secret
                      </h4>
                      <Button asChild variant="link" className="h-auto p-0 text-primary hover:text-primary/80 justify-start text-left whitespace-normal font-bold group/link">
                        <Link to={architect.paperLink}>
                          <span className="line-clamp-2">{architect.paperTitle}</span>
                          <ExternalLink className="size-3 ml-1 opacity-50 group-hover/link:opacity-100 transition-opacity shrink-0" />
                        </Link>
                      </Button>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>)}
        </div>
      </div>
    </section>;
}